import { useState, useEffect } from "react";
import { X } from "lucide-react";

export default function AddContactPersonModal({ open, onClose, onAdd, initialData = null }) {
  const empty = { 
    firstName: "", 
    lastName: "", 
    email: "", 
    phone: "", 
    mobile: "", 
    jobTitle: "", 
    department: "", 
    linkedIn: "", 
    avatarUrl: "", 
    primaryContact: false, 
    notes: "" 
  };
  
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) {
      setForm(empty);
      setError(null);
      setLoading(false);
      return;
    }
    
    if (initialData) {
      setForm({
        firstName: initialData.firstName || "",
        lastName: initialData.lastName || "",
        email: initialData.email || "",
        phone: initialData.phone || "",
        mobile: initialData.mobile || "",
        jobTitle: initialData.jobTitle || "",
        department: initialData.department || "",
        linkedIn: initialData.linkedIn || "",
        avatarUrl: initialData.avatarUrl || "",
        primaryContact: initialData.primaryContact || false,
        notes: initialData.notes || ""
      });
    } else {
      setForm(empty);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData, open]);

  const handleAdd = async () => {
    console.log("🟢 handleAdd called");
    console.log("📝 Form data:", form);
    console.log("📌 onAdd prop:", onAdd);
    
    // Validation
    if (!form.firstName || !form.lastName) {
      setError("First name and last name are required");
      return;
    }
    if (!form.email && !form.phone && !form.mobile) {
      setError("At least one of email, phone or mobile is required");
      return;
    }

    // Check if onAdd exists
    if (!onAdd) {
      console.error("❌❌❌ onAdd prop is UNDEFINED!");
      setError("Error: onAdd function not provided to modal");
      return;
    }

    setLoading(true);
    setError(null);
    console.log("📤 Calling onAdd with data:", form);
    
    try {
      const result = await onAdd({ ...form });
      console.log("✅ Person added successfully:", result);
      setForm(empty);
      onClose();
    } catch (error) {
      console.error("❌ Error in onAdd:", error);
      setError(error?.response?.data?.message || error?.message || "Failed to add contact person");
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.45)" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="flex flex-col w-full max-w-sm bg-white shadow-2xl rounded-2xl" style={{ maxHeight: "90vh" }}>

        <div className="flex items-center justify-between flex-shrink-0 px-6 py-4 border-b border-neutral-100">
          <h2 className="text-[15px] font-bold text-neutral-900">
            {initialData ? "Edit Contact Person" : "Add Contact Person"}
          </h2>
          <button onClick={onClose} className="flex items-center justify-center rounded-lg w-7 h-7 text-neutral-400 hover:bg-neutral-100">
            <X size={15} />
          </button>
        </div>

        <div className="flex flex-col flex-1 gap-3 px-6 py-4 overflow-y-auto">
          {error && (
            <div className="p-3 text-sm text-red-600 border border-red-200 rounded-lg bg-red-50">
              {error}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1">
              <label className="text-[11px] font-semibold text-neutral-500 uppercase tracking-wide">First Name *</label>
              <input
                value={form.firstName}
                onChange={(e) => setForm({...form, firstName: e.target.value})}
                placeholder="e.g. John"
                disabled={loading}
                className="h-9 border border-neutral-200 rounded-lg px-3 text-[13px] outline-none focus:border-[#009DB9] transition-colors disabled:bg-neutral-100"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-[11px] font-semibold text-neutral-500 uppercase tracking-wide">Last Name *</label>
              <input
                value={form.lastName}
                onChange={(e) => setForm({...form, lastName: e.target.value})}
                placeholder="e.g. Doe"
                disabled={loading}
                className="h-9 border border-neutral-200 rounded-lg px-3 text-[13px] outline-none focus:border-[#009DB9] transition-colors disabled:bg-neutral-100"
              />
            </div>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-[11px] font-semibold text-neutral-500 uppercase tracking-wide">Email</label>
            <input
              value={form.email}
              onChange={(e) => setForm({...form, email: e.target.value})}
              placeholder="john@company.com"
              disabled={loading}
              className="h-9 border border-neutral-200 rounded-lg px-3 text-[13px] outline-none focus:border-[#009DB9] transition-colors disabled:bg-neutral-100"
            />
            <p className="text-[9px] text-neutral-400">Email must be unique within the company</p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1">
              <label className="text-[11px] font-semibold text-neutral-500 uppercase tracking-wide">Phone</label>
              <input
                value={form.phone}
                onChange={(e) => setForm({...form, phone: e.target.value})}
                placeholder="+254 7XX XXX XXX"
                disabled={loading}
                className="h-9 border border-neutral-200 rounded-lg px-3 text-[13px] outline-none focus:border-[#009DB9] transition-colors disabled:bg-neutral-100"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-[11px] font-semibold text-neutral-500 uppercase tracking-wide">Mobile</label>
              <input
                value={form.mobile}
                onChange={(e) => setForm({...form, mobile: e.target.value})}
                placeholder="+254 7XX XXX XXX"
                disabled={loading}
                className="h-9 border border-neutral-200 rounded-lg px-3 text-[13px] outline-none focus:border-[#009DB9] transition-colors disabled:bg-neutral-100"
              />
            </div>
          </div>
          <p className="text-[9px] text-neutral-400 -mt-2">At least one of email, phone or mobile is required</p>

          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1">
              <label className="text-[11px] font-semibold text-neutral-500 uppercase tracking-wide">Job Title</label>
              <input
                value={form.jobTitle}
                onChange={(e) => setForm({...form, jobTitle: e.target.value})}
                placeholder="VP Engineering"
                disabled={loading}
                className="h-9 border border-neutral-200 rounded-lg px-3 text-[13px] outline-none focus:border-[#009DB9] transition-colors disabled:bg-neutral-100"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-[11px] font-semibold text-neutral-500 uppercase tracking-wide">Department</label>
              <input
                value={form.department}
                onChange={(e) => setForm({...form, department: e.target.value})}
                placeholder="Engineering"
                disabled={loading}
                className="h-9 border border-neutral-200 rounded-lg px-3 text-[13px] outline-none focus:border-[#009DB9] transition-colors disabled:bg-neutral-100"
              />
            </div>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-[11px] font-semibold text-neutral-500 uppercase tracking-wide">LinkedIn</label>
            <input
              value={form.linkedIn}
              onChange={(e) => setForm({...form, linkedIn: e.target.value})}
              placeholder="linkedin.com/in/johndoe"
              disabled={loading}
              className="h-9 border border-neutral-200 rounded-lg px-3 text-[13px] outline-none focus:border-[#009DB9] transition-colors disabled:bg-neutral-100"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-[11px] font-semibold text-neutral-500 uppercase tracking-wide">Notes</label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm({...form, notes: e.target.value})}
              placeholder="Additional notes..."
              rows={2}
              disabled={loading}
              className="border border-neutral-200 rounded-lg px-3 py-2 text-[13px] outline-none focus:border-[#009DB9] resize-none transition-colors disabled:bg-neutral-100"
            />
          </div>

          <div className="flex items-center gap-3 pt-1">
            <label className="text-[11px] font-semibold text-neutral-500 uppercase tracking-wide">Primary Contact</label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.primaryContact}
                onChange={(e) => setForm({...form, primaryContact: e.target.checked})}
                disabled={loading}
                className="w-4 h-4 rounded border-neutral-300 text-[#009DB9] focus:ring-[#009DB9] disabled:opacity-50"
              />
              <span className="text-[12px] text-neutral-600">Set as primary contact</span>
            </label>
          </div>
          <p className="text-[9px] text-neutral-400 -mt-1">The first person added becomes the primary contact automatically</p>
        </div>

        <div className="flex justify-end flex-shrink-0 gap-2 px-6 py-4 border-t border-neutral-100">
          <button 
            onClick={onClose}
            disabled={loading}
            className="px-4 h-8 rounded-lg text-[12px] font-medium text-neutral-600 border border-neutral-200 hover:bg-neutral-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button 
            onClick={handleAdd}
            disabled={!form.firstName || !form.lastName || (!form.email && !form.phone && !form.mobile) || loading}
            className="px-4 h-8 rounded-lg text-[12px] font-semibold text-white disabled:opacity-40 flex items-center gap-2"
            style={{ background: loading ? "#007a90" : "#009DB9" }}
          >
            {loading ? "Saving..." : (initialData ? "Save Changes" : "Add Person")}
          </button>
        </div>
      </div>
    </div>
  );
}